import { useMemo, useState } from 'react'
import type { DeviceSpecDevice, DeviceSpecTag, EdgeDevice, EdgeDeviceChannel } from '../../types'
import { channelDef } from './catalog'
import { useHoverTip } from './HoverTip'
import type { TipContent } from './HoverTip'
import { compareDevice, compareType, edgeTagValues, rowAction, summarize } from './specCompare'
import type { Direction, PlanState, RowAction, TagField } from './specCompare'

// 명세 ↔ Edge 비교 화면 — [Edge → 명세 가져오기]·[명세 → Edge 적용] 이 같이 쓴다. 기준은 명세: 늘 명세 값을 보여 준다.
// 색은 세 가지 뜻만: 빨강 = 실행하면 바뀌는 칸 · 초록 = 추가되는 태그 · 회색 취소선 + 빨간 "삭제" = 지워지는 태그.
// 주황 "Edge 누락" 은 명세에 있는 Alias 가 Edge 에 비어 있다는 표시 (가져오기는 바꾸지 않고, 적용하면 채워진다).
// 바뀌는 칸에 마우스를 올리면 Edge 값이 뜬다.

const TARGET: Record<Direction, string> = { pull: '명세', push: 'Edge' }

// 디바이스 목록의 "실행하면" 칸
export function PlanBadge({ plan, dir }: { plan: PlanState | undefined; dir: Direction }) {
  if (!plan) return null
  if (plan.kind === 'create') return <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700">{TARGET[dir]}에 새로 추가</span>
  if (plan.kind === 'pending') return <span className="text-gray-400">비교 중…</span>
  const s = plan.summary
  const missing = s.aliasEmpty > 0 && <span key="m" className="text-orange-600">Edge Alias 누락 {s.aliasEmpty}</span>
  if (plan.kind === 'same') {
    return (
      <div>
        <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">변경 없음</span>
        {missing && <div className="mt-1 text-[11px]">{missing}</div>}
      </div>
    )
  }
  const parts = [
    s.device > 0 && <span key="d" className="text-red-600">디바이스 {s.device}칸</span>,
    s.changed > 0 && <span key="c" className="text-red-600">태그 {s.changed}개 바뀜</span>,
    s.add > 0 && <span key="a" className="text-green-700">추가 {s.add}</span>,
    s.remove > 0 && <span key="r" className="text-red-700 font-semibold">삭제 {s.remove}</span>,
    missing,
  ].filter(Boolean)
  return (
    <div>
      <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-700">바뀜</span>
      {parts.length > 0 && <div className="mt-1 text-[11px] flex flex-wrap gap-x-1.5">{parts}</div>}
    </div>
  )
}

const TAG_COLUMNS: { key: TagField | 'name' | 'description'; label: string; mono?: boolean; className?: string }[] = [
  { key: 'name', label: '항목명' },
  { key: 'description', label: '설명' },
  { key: 'address', label: '주소', mono: true },
  { key: 'size', label: '크기', className: 'w-12' },
  { key: 'dataType', label: '타입' },
  { key: 'arithmetic', label: '연산', mono: true },
  { key: 'alias', label: 'Alias', mono: true },
]

const CHANGED_CELL = 'bg-red-50 text-red-700 font-medium cursor-help'

const ACTION_LABEL: Partial<Record<RowAction, { text: string; className: string }>> = {
  add: { text: '추가', className: 'text-green-700' },
  remove: { text: '삭제', className: 'text-red-700 font-semibold' },
  keep: { text: '명세에만', className: 'text-gray-400' },
}

export function ComparePreview({ dir, spec, edge, channel, keepSpecOnly }: {
  dir: Direction
  spec: DeviceSpecDevice | undefined
  edge: EdgeDevice | undefined
  channel: EdgeDeviceChannel | undefined // 같은 디바이스가 양쪽에 있을 때만 — 받는 중이면 undefined
  keepSpecOnly: boolean
}) {
  const [onlyChanged, setOnlyChanged] = useState(false)
  const { tipProps, tipNode, hideTip } = useHoverTip()
  const compare = useMemo(() => compareDevice(spec, edge, channel, compareType(spec, edge, channel, dir)), [spec, edge, channel, dir])
  const summary = summarize(compare, dir, keepSpecOnly)
  const both = !!spec && !!edge
  const rows = compare.rows.map(r => ({ r, ...rowAction(r, dir, keepSpecOnly) }))
  const shown = onlyChanged ? rows.filter(x => x.action !== 'same' && x.action !== 'keep' || x.r.edgeAliasEmpty) : rows

  const afterNote = dir === 'pull' ? '가져오면 이 값으로 바뀝니다' : '적용하면 명세 값으로 바뀝니다'
  const edgeTip = (value: string): TipContent => ({ title: 'Edge 의 지금 값', value, note: afterNote })
  const aliasTip: TipContent = dir === 'pull'
    ? { title: 'Edge Alias 누락', note: 'Edge 에는 Alias 가 비어 있습니다. 가져와도 명세 값이 남습니다. 서버 상세 › 디바이스 › [명세 → Edge 적용] 으로 채울 수 있습니다.' }
    : { title: 'Edge Alias 누락', note: 'Edge 에는 Alias 가 비어 있습니다. 적용하면 명세 값으로 채워집니다.' }

  // 디바이스 칸 — 이름·설명·프로토콜은 늘, 채널 설정은 다른 칸만. 값은 명세(없으면 Edge)
  const changedField = (label: string) => compare.fields.find(f => f.label === label)
  const shownValue = (secret: boolean | undefined, v: string) => (secret && v ? '••••' : v)
  const base = spec
    ? { name: spec.name, description: spec.description, protocol: channelDef(spec.channelType)?.label ?? spec.channelType }
    : { name: edge?.name ?? '', description: edge?.description ?? '', protocol: channelDef(edge?.channelType ?? '')?.label ?? edge?.channelType ?? '' }
  const deviceRows = [
    { label: '디바이스 이름', value: base.name, change: changedField('디바이스 이름') },
    { label: '설명', value: base.description, change: changedField('설명') },
    { label: '프로토콜', value: base.protocol, change: changedField('프로토콜') },
    ...compare.fields.filter(f => !['디바이스 이름', '설명', '프로토콜'].includes(f.label))
      .map(f => ({ label: f.label, value: shownValue(f.secret, f.spec), change: f })),
  ]

  const removeNote = summary.remove > 0 && (dir === 'pull'
    ? `Edge 에 없는 명세 태그 ${summary.remove}개가 명세에서 삭제됩니다.`
    : `Edge 태그 ${summary.remove}개가 삭제되고, 그 태그를 쓰던 마스터데이터 연결이 끊깁니다.`)

  return (
    <div>
      <div className="px-4 py-3 border-b border-gray-100 sticky top-0 bg-surface z-10">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-800">{base.name || '(이름 없음)'}</span>
          <span className="font-mono text-xs text-gray-400">{spec?.code ?? edge?.code}</span>
          {!both && <span className="ml-auto text-[11px] px-1.5 py-0.5 rounded bg-green-50 text-green-700">{TARGET[dir]}에 새로 추가</span>}
        </div>
        <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
          {deviceRows.map(d => (
            <div key={d.label} className="contents">
              <dt className="text-gray-400 py-0.5">{d.label}</dt>
              <dd {...tipProps(d.change && edgeTip(shownValue(d.change.secret, d.change.edge)))}
                className={`px-1.5 py-0.5 rounded break-all ${d.change ? CHANGED_CELL : 'text-gray-700'}`}>
                {d.value || <span className={d.change ? 'text-red-300' : 'text-gray-300'}>(비어 있음)</span>}
              </dd>
            </div>
          ))}
        </dl>
        {both && !channel && <div className="mt-1 text-[11px] text-gray-400">채널 설정을 받는 중… (받으면 접속 정보도 견줍니다)</div>}

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
          <span className="text-gray-500">태그 {spec?.tags.length ?? edge?.tags.length ?? 0}</span>
          {summary.changed > 0 && <span className="text-red-600">바뀜 {summary.changed}</span>}
          {both && summary.add > 0 && <span className="text-green-700">추가 {summary.add}</span>}
          {summary.remove > 0 && <span className="text-red-700 font-semibold">삭제 {summary.remove}</span>}
          {summary.aliasEmpty > 0 && <span className="text-orange-600">Edge Alias 누락 {summary.aliasEmpty}</span>}
          {both && (
            <label className="ml-auto flex items-center gap-1.5 text-gray-600 cursor-pointer select-none">
              <input type="checkbox" checked={onlyChanged} onChange={e => setOnlyChanged(e.target.checked)} />
              달라진 것만 보기
            </label>
          )}
        </div>
        {removeNote && <div className="mt-2 text-[11px] text-red-700 bg-red-50 rounded px-2 py-1">{removeNote}</div>}
        {summary.aliasEmpty > 0 && dir === 'pull' && (
          <div className="mt-2 text-[11px] text-orange-700 bg-orange-50 rounded px-2 py-1">
            Edge 에 Alias 가 비어 있는 태그가 {summary.aliasEmpty}개 있습니다. 서버 상세 › 디바이스 › [명세 → Edge 적용] 으로 채울 수 있습니다.
          </div>
        )}
      </div>

      <table className="w-full text-xs" onMouseLeave={hideTip}>
        <thead className="bg-gray-50 text-gray-500">
          <tr>
            <th className="w-1 p-0" />
            <th className="text-left px-2 py-2 w-8">#</th>
            {TAG_COLUMNS.map(c => <th key={c.key} className={`text-left px-2 py-2 ${c.className ?? ''}`}>{c.label}</th>)}
            {both && <th className="w-14" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {shown.map(({ r, action, changed }) => {
            const tag: Pick<DeviceSpecTag, TagField | 'name' | 'description'> = r.spec ?? { name: '', description: '', ...edgeTagValues(r.edge!) }
            const edgeValues = r.edge ? edgeTagValues(r.edge) : null
            const removed = action === 'remove'
            const added = action === 'add' && both
            const bar = action === 'change' ? 'bg-red-400' : added ? 'bg-green-400' : removed ? 'bg-gray-300' : ''
            const label = both ? ACTION_LABEL[action] : undefined
            return (
              <tr key={r.key} className={removed ? 'text-gray-400' : added ? 'bg-green-50/40' : ''}>
                <td className={`p-0 ${bar}`} />
                <td className="px-2 py-1.5 text-gray-300 font-mono">{rows.findIndex(x => x.r === r) + 1}</td>
                {TAG_COLUMNS.map(c => {
                  const isChanged = c.key !== 'name' && c.key !== 'description' && changed.includes(c.key)
                  const aliasEmpty = c.key === 'alias' && r.edgeAliasEmpty
                  const tip = aliasEmpty ? aliasTip : isChanged && edgeValues ? edgeTip(edgeValues[c.key as TagField]) : undefined
                  return (
                    <td key={c.key} {...tipProps(tip)}
                      className={`px-2 py-1.5 ${c.mono ? 'font-mono' : ''} ${isChanged ? CHANGED_CELL : removed ? 'line-through' : 'text-gray-700'} ${tip ? 'cursor-help' : ''}`}>
                      {tag[c.key] || (isChanged ? <span className="text-red-300">(비어 있음)</span> : '')}
                      {aliasEmpty && <span className="ml-1.5 align-middle font-sans text-[10px] px-1 rounded border border-orange-300 text-orange-600 bg-orange-50">Edge 누락</span>}
                    </td>
                  )
                })}
                {both && <td className={`px-2 py-1.5 text-[11px] ${label?.className ?? ''}`}>{label?.text}</td>}
              </tr>
            )
          })}
          {shown.length === 0 && (
            <tr><td colSpan={TAG_COLUMNS.length + 3} className="p-6 text-center text-gray-400">{onlyChanged ? '달라진 태그가 없습니다.' : '태그가 없습니다.'}</td></tr>
          )}
        </tbody>
      </table>
      {tipNode}
    </div>
  )
}
